import { Link } from 'react-router-dom';
import { useState } from 'react';

const exploreLinks = [
  { label: 'Home', path: '/' },
  { label: 'Our Services', path: '/services' },
  { label: 'In-Home Programs', path: '/in-home-programs' },
  { label: 'Off-Site Services', path: '/off-site-services' },
  { label: 'Recording Studio', path: '/recording-studio' },
  { label: 'Neurodivergent Development', path: '/neurodivergent-development' },
  { label: 'Cities We Serve', path: '/cities-we-serve' },
  { label: 'Blog & Tips', path: '/blog' },
];

const supportLinks = [
  { label: 'Call for Pricing: 910-242-4567', path: 'tel:9102424567' },
  { label: 'Gift Cards', path: '/gift-cards' },
  { label: 'Ministry Connections', path: '/ministry-connections' },
  { label: 'Enroll Now', path: '/enroll' },
  { label: 'Work With Us', path: '/client-portal' },
  { label: 'Refer a Friend', path: '/refer-a-friend' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Meet the Founder', path: '/meet-the-founder' },
  { label: 'Contact Us', path: '/contact' },
];

const socialLinks = [
  { icon: 'ri-facebook-fill', label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61576477811565' },
];

export default function Footer() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('https://creativeinstitutellc.com').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareUrl = encodeURIComponent('https://creativeinstitutellc.com');
  const shareText = encodeURIComponent('Check out Creative Institute — an amazing creative arts program serving multiple cities across North Carolina! Where Every Creative Mind Matters.');

  return (
    <footer className="bg-[#0F172A] text-white">
      {/* Share strip */}
      <div className="border-b border-white/8 py-8">
        <div className="max-w-screen-xl mx-auto px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-heading text-white font-bold text-base mb-1">Spread the Word</p>
            <p className="font-body text-white/50 text-sm">Help us reach more creatives across North Carolina and beyond.</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/15 border border-[#1877F2]/30 text-[#6EA8FE] font-body text-xs font-semibold hover:bg-[#1877F2]/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-facebook-fill text-sm" /> Share on Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 border border-white/15 text-white/70 font-body text-xs font-semibold hover:bg-white/12 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-twitter-x-line text-sm" /> Share on X
            </a>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow font-body text-xs font-semibold hover:bg-brand-yellow/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line text-sm' : 'ri-link text-sm'} />
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>

      {/* Main footer body */}
      <div className="max-w-screen-xl mx-auto px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand col */}
        <div className="lg:col-span-1">

          <p className="font-body text-white/50 text-sm leading-relaxed mb-6">
            Where Every Creative Mind Matters. A mobile-based creative development program serving multiple cities across North Carolina — we come to you.
          </p>
          {/* Social icons */}
          <div className="flex items-center gap-3">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="nofollow noreferrer"
                aria-label={s.label}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/8 border border-white/10 text-white/60 hover:bg-brand-yellow hover:text-brand-blue-dark hover:border-brand-yellow transition-all duration-300 cursor-pointer"
              >
                <i className={`${s.icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>

        {/* Explore links */}
        <div>
          <h4 className="font-heading text-white font-bold text-sm uppercase tracking-widest mb-5">
            <a id="explore">Explore</a>
          </h4>
          <ul className="flex flex-col gap-3">
            {exploreLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className="font-body text-white/50 text-sm hover:text-brand-yellow transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Support links */}
        <div>
          <h4 className="font-heading text-white font-bold text-sm uppercase tracking-widest mb-5">
            <a id="support">Support</a>
          </h4>
          <ul className="flex flex-col gap-3">
            {supportLinks.map((link) => (
              <li key={link.path}>
                {link.path.startsWith('tel:') ? (
                  <a
                    href={link.path}
                    className="font-body text-brand-yellow text-sm hover:text-brand-yellow-light transition-colors cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-phone-line text-xs" />
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    className="font-body text-white/50 text-sm hover:text-brand-yellow transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Contact info */}
        <div>
          <h4 className="font-heading text-white font-bold text-sm uppercase tracking-widest mb-5">
            <a id="contact-info">Contact</a>
          </h4>
          <div className="flex flex-col gap-4">
            <a href="tel:9102424567" className="flex items-start gap-3 group cursor-pointer">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/8 text-brand-yellow flex-shrink-0 mt-0.5 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
                <i className="ri-phone-fill text-sm" />
              </div>
              <div>
                <p className="font-body text-white/40 text-xs uppercase tracking-widest mb-0.5">Phone</p>
                <p className="font-body text-white/70 text-sm group-hover:text-brand-yellow transition-colors">910-242-4567</p>
              </div>
            </a>
            <a href="mailto:info@creativeinstitutellc.com" className="flex items-start gap-3 group cursor-pointer">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/8 text-brand-yellow flex-shrink-0 mt-0.5 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300">
                <i className="ri-mail-fill text-sm" />
              </div>
              <div>
                <p className="font-body text-white/40 text-xs uppercase tracking-widest mb-0.5">Email</p>
                <p className="font-body text-white/70 text-sm group-hover:text-brand-yellow transition-colors break-all">info@creativeinstitutellc.com</p>
              </div>
            </a>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/8 text-brand-yellow flex-shrink-0 mt-0.5">
                <i className="ri-map-pin-fill text-sm" />
              </div>
              <div>
                <p className="font-body text-white/40 text-xs uppercase tracking-widest mb-0.5">Service Area</p>
                <p className="font-body text-white/70 text-sm">Multiple Cities Across North Carolina</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/8 text-brand-yellow flex-shrink-0 mt-0.5">
                <i className="ri-time-fill text-sm" />
              </div>
              <div>
                <p className="font-body text-white/40 text-xs uppercase tracking-widest mb-0.5">Hours</p>
                <p className="font-body text-white/70 text-sm">Mon – Sat: 9am – 9pm</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/8 py-6">
        <div className="max-w-screen-xl mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-white/30 text-xs text-center sm:text-left">
            © {new Date().getFullYear()} Creative Institute LLC. All rights reserved. Serving Multiple Cities Across North Carolina.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="font-body text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">Privacy Policy</Link>
            <Link to="/contact" className="font-body text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
